/**
 * Delete my account and data: see what goes and what stays, retype the phrase,
 * and only then (#373, D30).
 *
 * The same shape as the founder's whole-installation panel and for the same
 * reasons - the lists come from the preview the server returned, the phrase and
 * the token come from the server too, the dialog is not time-limited - but about
 * ONE person: the signed-in learner. It names tables and presence, never a row or
 * a count, and it says plainly what survives (a dated sign-up, audit rows that
 * matched no account, backups until they expire, anything a provider already
 * holds). Those lists are the server's, so a residue added there appears here
 * with no change to this file.
 *
 * The two named refusals arrive from the PREVIEW, before there is a token to
 * confirm, and are shown in words that say what to do instead: the founder is
 * pointed at the whole-installation deletion; an importer whose records course
 * material cites is told it cannot be done yet.
 */

import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LearnerDeletionPreviewResponse, LearnerDeletionResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    heading: 'Delete my account and data',
    intro1:
      'This removes your account, your sign-in, everything you did, and the files you uploaded, from the live server. There is no undo. It does not touch anyone else\u2019s.',
    intro2: 'DaVinci will show you what it is about to remove, and what it cannot, before you decide.',
    checking: 'Checking…',
    show: 'Show me what deleting my account would do',
    deleteEllipsis: 'Delete my account and data…',
    dialogTitle: 'Delete your account and everything you did?',
    dialogBody:
      'Everything listed on this page will be removed and you will be signed out. To use DaVinci again you would need a new invite.',
    phraseMismatch: (phrase: string) =>
      `The phrase does not match. Type ${phrase} in the box below, exactly, or press Cancel.`,
    typeToConfirm: (phrase: string) => `Type ${phrase} to confirm`,
    retyping: 'Retyping it is what separates deciding from clicking.',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    deleteConfirm: 'Delete my account and data',
    founderRefused:
      'This account is the one the install is set up under, so it cannot be deleted from here: it is the account everything else hangs from. To remove DaVinci and everyone\u2019s data, use Delete everything instead. Nothing was deleted.',
    provenanceRefused:
      'This account owns imported course material that other people\u2019s records cite, so it cannot be deleted yet. Nothing was deleted. It can be removed once that material is withdrawn. Ask DaVinci’s operator.',
    expired:
      'This confirmation is no longer valid \u2014 it expired, or a newer preview replaced it. Nothing was deleted. Close this and show the list again.',
    notAccepted: 'DaVinci did not accept that phrase. Nothing was deleted.',
    wouldBeRemoved: 'What would be removed',
    noTables: 'None of your tables hold anything yet.',
    filesRemoved: 'Files that only your records name would be deleted with them.',
    noFiles: 'You have no uploaded files that only your records name.',
    filesKept: 'A file that something else still names would be kept: it is not only yours.',
    stays: 'What stays, by design',
    removedAccount: 'Your account and sign-in',
    removedActivity: 'Everything you did in DaVinci',
    removedFiles: 'The files you uploaded',
    fullList: 'The full list, table by table',
    fullListNote:
      'Every table this deletes, and the full reason for each thing that stays, exactly as the server reports them.',
    fullRemoved: 'Tables that would be emptied',
    fullStays: 'What stays, with the full reason',
    fullCannot: 'What DaVinci cannot delete, in full',
    staysInvites: 'When the invite was used to sign up',
    staysAudit: 'Audit rows that name no one',
    staysSpend: 'Spend records, under a random label',
    staysBindings: 'Shared settings that are not personal data',
    staysBackups: 'Backups, until they expire',
    staysRequestLog: 'Record ids in the request log',
    staysStaging: 'Staging snapshots',
    cannotSent: 'What was already sent to a provider',
    cannotSsd: 'Remnants on the SSD',
    cannotDelete: 'What DaVinci cannot delete for you',
    expiresAfter: (time: string) =>
      `This confirmation stops working shortly after ${time}. Nothing on this screen closes itself \u2014 if it expires while you are reading, ask for the list again.`,
  },
  he: {
    heading: 'מחיקת החשבון והנתונים שלי',
    intro1:
      'פעולה זו מסירה מהשרת החי את החשבון שלך, את ההתחברות שלך, את כל מה שעשית ואת הקבצים שהעלית. אין ביטול. היא לא נוגעת בשום דבר של אף אחד אחר.',
    intro2: 'DaVinci יראה לך מה הוא עומד להסיר, ומה הוא לא יכול, לפני ההחלטה.',
    checking: 'בדיקה…',
    show: 'הצגת מה תעשה מחיקת החשבון שלי',
    deleteEllipsis: 'מחיקת החשבון והנתונים שלי…',
    dialogTitle: 'למחוק את החשבון שלך ואת כל מה שעשית?',
    dialogBody:
      'כל מה שמופיע בעמוד הזה יוסר, והחיבור יתנתק. שימוש חוזר ב-DaVinci ידרוש הזמנה חדשה.',
    phraseMismatch: (phrase: string) =>
      `הביטוי לא תואם. יש להקליד ${phrase} בתיבה שלמטה, בדיוק כפי שהוא, או ללחוץ על ביטול.`,
    typeToConfirm: (phrase: string) => `יש להקליד ${phrase} לאישור`,
    retyping: 'הקלדה מחדש היא מה שמבדיל בין החלטה ללחיצה.',
    cancel: 'ביטול',
    deleting: 'מחיקה…',
    deleteConfirm: 'מחיקת החשבון והנתונים שלי',
    founderRefused:
      'זה החשבון שההתקנה הוגדרה תחתיו, ולכן אי אפשר למחוק אותו מכאן: זה החשבון שכל השאר תלוי בו. כדי להסיר את DaVinci ואת הנתונים של כולם, יש להשתמש במקום זאת ב"מחיקת הכול". שום דבר לא נמחק.',
    provenanceRefused:
      'בחשבון הזה יש חומר קורס מיובא שהרשומות של אחרים מצטטות, ולכן אי אפשר למחוק אותו עדיין. שום דבר לא נמחק. אפשר להסיר אותו אחרי שהחומר הזה יוסר. כדאי לפנות למפעיל של ⁨DaVinci⁩.',
    expired:
      'האישור הזה כבר לא תקף \u2014 הוא פג, או שתצוגה מקדימה חדשה יותר החליפה אותו. שום דבר לא נמחק. יש לסגור את החלון ולהציג את הרשימה שוב.',
    notAccepted: 'DaVinci לא קיבל את הביטוי הזה. שום דבר לא נמחק.',
    wouldBeRemoved: 'מה יוסר',
    noTables: 'עדיין אין בטבלאות שלך שום דבר.',
    filesRemoved: 'קבצים ששמורים רק ברשומות שלך יימחקו יחד איתן.',
    noFiles: 'אין לך קבצים שהועלו ושמורים רק ברשומות שלך.',
    filesKept: 'קובץ שעדיין יש לו התייחסות ממקום אחר יישמר: הוא לא שלך בלבד.',
    stays: 'מה שנשאר, בכוונה',
    removedAccount: 'החשבון וההתחברות',
    removedActivity: 'כל מה שנעשה ב-DaVinci',
    removedFiles: 'הקבצים שהועלו',
    fullList: 'הרשימה המלאה, טבלה אחר טבלה',
    fullListNote: 'כל טבלה שנמחקת, והסיבה המלאה לכל מה שנשאר, בדיוק כפי שהשרת מדווח.',
    fullRemoved: 'טבלאות שיתרוקנו',
    fullStays: 'מה שנשאר, עם הסיבה המלאה',
    fullCannot: 'מה ש-DaVinci לא יכולה למחוק, במלואו',
    staysInvites: 'מועד ההרשמה בהזמנה',
    staysAudit: 'רישומי ביקורת ללא שיוך',
    staysSpend: 'רישומי עלות, תחת תווית אקראית',
    staysBindings: 'הגדרות משותפות שאינן מידע אישי',
    staysBackups: 'גיבויים, עד שתוקפם פג',
    staysRequestLog: 'מזהי רשומות ביומן הבקשות',
    staysStaging: 'תמונות מצב בשלב ההכנה',
    cannotSent: 'מה שכבר נשלח לספק',
    cannotSsd: 'שאריות בכונן SSD',
    cannotDelete: 'מה ש-DaVinci לא יכול למחוק בשבילך',
    expiresAfter: (time: string) =>
      `האישור הזה מפסיק לעבוד זמן קצר אחרי ${time}. שום דבר במסך הזה לא נסגר מעצמו \u2014 אם הוא פג בזמן הקריאה, יש לבקש את הרשימה שוב.`,
  },
});

export interface MyDeletionPanelProps {
  /** Called with the receipt once the account is gone. */
  readonly onDeleted: (receipt: LearnerDeletionResponse) => void;
}

export function MyDeletionPanel({ onDeleted }: MyDeletionPanelProps): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const queryClient = useQueryClient();
  const headingId = useId();
  const dialogHeadingId = useId();
  const dialogBodyId = useId();
  const phraseFieldId = useId();

  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [phraseRejected, setPhraseRejected] = useState(false);

  const preview = useMutation({
    mutationFn: () => api.previewMyDeletion(),
    onSuccess: () => {
      setConfirming(false);
      setPhrase('');
      setPhraseRejected(false);
    },
  });

  const confirm = useMutation({
    mutationFn: (body: { token: string; confirmationPhrase: string }) => api.deleteMyAccount(body),
    onSuccess: (receipt) => {
      setConfirming(false);
      // The session is already ended on the server. Nothing cached here may outlive it:
      // the account control, the progress page and the rest were all about this person.
      queryClient.clear();
      onDeleted(receipt);
    },
  });

  const listing = preview.data;

  function handleConfirm(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (listing === undefined) return;
    if (phrase.trim() !== listing.confirmationPhrase) {
      setPhraseRejected(true);
      return;
    }
    setPhraseRejected(false);
    confirm.mutate({ token: listing.token.value, confirmationPhrase: phrase.trim() });
  }

  return (
    <section className="panel panel-destructive" aria-labelledby={headingId}>
      <h2 id={headingId}>{t.heading}</h2>
      <p>{t.intro1}</p>
      <p>{t.intro2}</p>

      <button type="button" className="button-neutral settings-button" onClick={() => preview.mutate()} disabled={preview.isPending}>
        {preview.isPending ? t.checking : t.show}
      </button>

      {preview.isError ? <PreviewFailure error={preview.error} onRetry={() => preview.mutate()} /> : null}

      {listing !== undefined ? (
        <div className="panel-result">
          <MyDeletionListing listing={listing} bodyId={dialogBodyId} />

          <button type="button" className="button-destructive settings-button" onClick={() => setConfirming(true)}>
            {t.deleteEllipsis}
          </button>

          {confirm.isError && !confirming ? <DeletionFailure error={confirm.error} /> : null}
        </div>
      ) : null}

      {confirming && listing !== undefined ? (
        <ConfirmDialog
          labelledBy={dialogHeadingId}
          describedBy={dialogBodyId}
          onDismiss={() => setConfirming(false)}
        >
          <h2 id={dialogHeadingId}>{t.dialogTitle}</h2>
          <p>{t.dialogBody}</p>

          {confirm.isError ? <DeletionFailure error={confirm.error} /> : null}

          {phraseRejected ? (
            <div className="notice notice-error" role="alert">
              <p>{t.phraseMismatch(listing.confirmationPhrase)}</p>
            </div>
          ) : null}

          <form onSubmit={handleConfirm} noValidate>
            <div className="field">
              <label htmlFor={phraseFieldId}>
                {t.typeToConfirm(listing.confirmationPhrase)}
              </label>
              <p className="field-hint" id={`${phraseFieldId}-hint`}>
                {t.retyping}
              </p>
              <input
                id={phraseFieldId}
                name="confirmationPhrase"
                type="text"
                value={phrase}
                autoComplete="off"
                aria-describedby={`${phraseFieldId}-hint`}
                onChange={(event) => {
                  setPhrase(event.target.value);
                  setPhraseRejected(false);
                }}
              />
            </div>

            <div className="dialog-actions">
              <button type="button" onClick={() => setConfirming(false)}>
                {t.cancel}
              </button>
              <button type="submit" className="button-destructive" disabled={confirm.isPending}>
                {confirm.isPending ? t.deleting : t.deleteConfirm}
              </button>
            </div>
          </form>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

/** The two named refusals say what to do instead; anything else is the generic notice. */
function PreviewFailure({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry: () => void;
}): React.JSX.Element {
  const t = useMessages(messages);
  if (error instanceof ApiRequestError && error.code === 'founder_deletion_refused') {
    return (
      <div className="notice" role="alert">
        <p>{t.founderRefused}</p>
      </div>
    );
  }
  if (error instanceof ApiRequestError && error.code === 'import_provenance_deletion_refused') {
    return (
      <div className="notice" role="alert">
        <p>{t.provenanceRefused}</p>
      </div>
    );
  }
  return <FailureNotice error={error} onRetry={onRetry} />;
}

function DeletionFailure({ error }: { readonly error: unknown }): React.JSX.Element {
  const t = useMessages(messages);
  if (error instanceof ApiRequestError && error.code === 'confirmation_expired') {
    return (
      <div className="notice notice-error" role="alert">
        <p>{t.expired}</p>
      </div>
    );
  }
  if (error instanceof ApiRequestError && error.code === 'confirmation_phrase_mismatch') {
    return (
      <div className="notice notice-error" role="alert">
        <p>{t.notAccepted}</p>
      </div>
    );
  }
  return <PreviewFailure error={error} onRetry={() => undefined} />;
}

type Copy = (typeof messages)['en'];
type PlainKey = {
  [K in keyof Copy]: Copy[K] extends string ? K : never;
}[keyof Copy];

/**
 * The plain words for what the server reports (UX round 3). A summary drawn only from the report, never in place of it: every table,
 * every reason and every sentence is still in the closed "full list" disclosure below it, word for word.
 */
const RETAINED_PLAIN: Readonly<Record<string, PlainKey>> = {
  invites: 'staysInvites',
  auth_events: 'staysAudit',
  budget_reservations: 'staysSpend',
  policy_bindings: 'staysBindings',
};

/** The server's `outsideOurControl` sentences, found by their exact opening words (`LEARNER_OUTSIDE_OUR_CONTROL`, data/learner-delete.ts). */
const CONTROL_PLAIN: readonly { readonly opening: string; readonly key: PlainKey; readonly column: 'stays' | 'cannot' }[] = [
  { opening: 'The server is backed up every night.', key: 'staysBackups', column: 'stays' },
  { opening: "The server's request log keeps the path of each request", key: 'staysRequestLog', column: 'stays' },
  { opening: 'The nightly backup makes a copy of the database', key: 'staysStaging', column: 'stays' },
  { opening: 'Anything already sent to an AI service', key: 'cannotSent', column: 'cannot' },
  { opening: 'Files DaVinci removes are overwritten in place', key: 'cannotSsd', column: 'cannot' },
];

function MyDeletionListing({
  listing,
  bodyId,
}: {
  readonly listing: LearnerDeletionPreviewResponse;
  readonly bodyId: string;
}): React.JSX.Element {
  const t = useMessages(messages);

  const removed = [
    t.removedAccount,
    ...(listing.tablesHoldingData.length > 0 ? [t.removedActivity] : []),
    ...(listing.removesUploadedFiles ? [t.removedFiles] : []),
  ];

  // A sentence with no mapping is shown verbatim and in full, in the column the server's list puts it in (Cannot).
  const mapped = listing.outsideOurControl.map((sentence) => ({
    sentence,
    rule: CONTROL_PLAIN.find((candidate) => sentence.startsWith(candidate.opening)),
  }));
  const stays = [
    ...listing.retainedByDesign.map((row) => {
      const key = RETAINED_PLAIN[row.table];
      return { id: `table-${row.table}`, text: key === undefined ? row.table : t[key] };
    }),
    ...mapped.flatMap((m) => (m.rule?.column === 'stays' ? [{ id: `control-${m.sentence}`, text: t[m.rule.key] }] : [])),
  ];
  const cannot = mapped
    .filter((m) => m.rule?.column !== 'stays')
    .map((m) => ({ id: `control-${m.sentence}`, text: m.rule === undefined ? m.sentence : t[m.rule.key] }));

  return (
    <div id={bodyId}>
      <div className="settings-del-grid">
        <div className="settings-del-col" data-col="removed">
          <h3>{t.wouldBeRemoved}</h3>
          <ul className="plain-list">
            {removed.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {!listing.removesUploadedFiles || listing.keepsSharedFiles ? (
            <p>
              {listing.removesUploadedFiles ? null : t.noFiles} {listing.keepsSharedFiles ? t.filesKept : null}
            </p>
          ) : null}
        </div>
        <div className="settings-del-col">
          <h3>{t.stays}</h3>
          <ul className="plain-list">
            {stays.map((item) => (
              <li key={item.id}>{item.text}</li>
            ))}
          </ul>
        </div>
        <div className="settings-del-col">
          <h3>{t.cannotDelete}</h3>
          <ul className="plain-list">
            {cannot.map((item) => (
              <li key={item.id} dir="auto">
                {item.text}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <details className="settings-del-full">
        <summary>{t.fullList}</summary>
        <p className="field-hint">{t.fullListNote}</p>
        <h4>{t.fullRemoved}</h4>
        {listing.tablesHoldingData.length > 0 ? (
          <ul className="plain-list">
            {listing.tablesHoldingData.map((table) => (
              <li key={table}>
                <code>{table}</code>
              </li>
            ))}
          </ul>
        ) : (
          <p>{t.noTables}</p>
        )}
        <p>
          {listing.removesUploadedFiles ? t.filesRemoved : t.noFiles} {listing.keepsSharedFiles ? t.filesKept : null}
        </p>
        <h4>{t.fullStays}</h4>
        <ul className="plain-list">
          {listing.retainedByDesign.map((row) => (
            <li key={row.table}>
              <code>{row.table}</code> — {row.reason}
            </li>
          ))}
        </ul>
        <h4>{t.fullCannot}</h4>
        <ul className="plain-list">
          {listing.outsideOurControl.map((limit) => (
            <li key={limit}>{limit}</li>
          ))}
        </ul>
      </details>

      <p>{t.expiresAfter(utcTimeOf(listing.token.expiresAt))}</p>
    </div>
  );
}

/** See the note on the identical helper in export-panel.tsx. */
function utcTimeOf(iso: string): string {
  return `${iso.slice(11, 19)} UTC`;
}
